import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  UserIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import logo from "../assets/logo.jpeg";
import fuego from "../assets/fuego.jpg";
import { inicializarAudio } from "../utils/sonidoAlerta";

const AUTH_URL     = "https://backcvbgtmdesa.azurewebsites.net/api/login/authenticate";
const BOMBERO_URL  = "/api/auth/bombero";


export default function Login() {
  const [modo, setModo]     = useState("mando"); // "mando" | "bombero"
  const [form, setForm]     = useState({ username: "", password: "" });
  const [bForm, setBForm]   = useState({ carnet: "", password: "" });
  const [error, setError]   = useState("");
  const [cargando, setCargando] = useState(false);
  const [verPass, setVerPass]   = useState(false);
  const { login }  = useAuth();
  const navigate   = useNavigate();
  const formRef    = useRef(null);

  const shake = () => {
    if (formRef.current) {
      formRef.current.classList.add("shake");
      setTimeout(() => formRef.current?.classList.remove("shake"), 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    try {
      const { data } = await axios.post(AUTH_URL, form, {
        headers: { "Content-Type": "application/json" },
      });
      login(data);
      inicializarAudio();
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.msgRespuesta || "Credenciales incorrectas. Verifica e intenta de nuevo.");
      shake();
    } finally {
      setCargando(false);
    }
  };

  const handleBomberoSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!bForm.carnet.trim() || !bForm.password.trim()) {
      setError("Carnet y contraseña son requeridos.");
      return;
    }
    setCargando(true);
    try {
      const { data } = await axios.post(BOMBERO_URL, {
        carnet:   bForm.carnet.trim().toUpperCase(),
        password: bForm.password,
      });
      login(data);
      inicializarAudio();
      navigate("/despacho");
    } catch (err) {
      setError(err.response?.data?.msgRespuesta || "Contraseña incorrecta. Consulta a tu comandante.");
      shake();
    } finally {
      setCargando(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after {
          box-sizing: border-box;
        }

        body {
          margin: 0;
        }

        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background-image: url(${fuego});
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          position: relative;
        }

        .login-page::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.45);
          pointer-events: none;
        }

        .login-card {
          width: 100%;
          max-width: 850px;
          border-radius: 24px;
          background: rgba(0, 0, 0, 0.15);
          backdrop-filter: blur(10px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr 1.3fr;
          position: relative;
          z-index: 1;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .login-left {
          padding: 60px 50px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: transparent;
          border-right: 1px solid rgba(255, 255, 255, 0.15);
        }

        .login-logo {
          width: 260px;
          height: 260px;
          border-radius: 50%;
          overflow: hidden;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5), 0 0 0 3px rgba(255, 255, 255, 0.2);
        }

        .login-logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .login-right {
          padding: 60px 50px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          background: transparent;
        }

        .login-title {
          margin: 0 0 6px;
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 700;
          text-align: center;
          letter-spacing: -0.01em;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        .login-subtitle {
          margin: 0 0 30px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.875rem;
          text-align: center;
        }

        .role-picker {
          display: flex;
          gap: 0;
          margin-bottom: 22px;
          background: rgba(0,0,0,0.3);
          border-radius: 10px;
          padding: 4px;
          border: 1px solid rgba(255,255,255,0.12);
        }

        .role-tab {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 9px 10px;
          border-radius: 7px;
          border: none;
          background: transparent;
          color: rgba(255,255,255,0.5);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          transition: all .2s ease;
          font-family: inherit;
        }

        .role-tab svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .role-tab.active {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          color: #fff;
          font-weight: 600;
          box-shadow: 0 2px 10px rgba(220,38,38,0.35);
        }

        .role-tab:hover:not(.active) {
          color: rgba(255,255,255,0.85);
          background: rgba(255,255,255,0.07);
        }

        .login-form {
          width: 100%;
        }

        .field {
          margin-bottom: 20px;
        }

        .field label {
          display: block;
          margin-bottom: 8px;
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.875rem;
          font-weight: 600;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          color: rgba(255, 255, 255, 0.5);
          pointer-events: none;
        }

        .input-icon svg {
          width: 19px;
          height: 19px;
        }

        .input-wrapper input {
          width: 100%;
          padding: 12px 14px 12px 42px;
          border: 1.5px solid rgba(255, 255, 255, 0.3);
          border-radius: 8px;
          font-size: 0.9375rem;
          color: #ffffff;
          font-family: inherit;
          transition: all .2s ease;
          background: rgba(0, 0, 0, 0.3);
        }

        .input-wrapper input:focus {
          outline: none;
          border-color: rgba(255, 255, 255, 0.5);
          box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
          background: rgba(0, 0, 0, 0.4);
        }

        .input-wrapper input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        .input-wrapper.has-action input {
          padding-right: 42px;
        }

        .input-action {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: rgba(255, 255, 255, 0.6);
          transition: color .2s ease;
        }

        .input-action:hover {
          color: rgba(255, 255, 255, 0.9);
        }

        .input-action svg {
          width: 19px;
          height: 19px;
        }

        .login-button {
          width: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: none;
          border-radius: 8px;
          padding: 13px;
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          color: #ffffff;
          font-size: 0.9375rem;
          font-weight: 600;
          cursor: pointer;
          transition: all .2s ease;
          margin-top: 8px;
          box-shadow: 0 4px 15px rgba(220, 38, 38, 0.4);
        }

        .login-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(220, 38, 38, 0.5);
        }

        .login-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .login-button svg {
          width: 18px;
          height: 18px;
        }

        .alert-box {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 16px;
          padding: 11px 13px;
          border-radius: 8px;
          background: rgba(254, 242, 242, 0.95);
          color: #b91c1c;
          font-size: 0.875rem;
          border: 1px solid rgba(252, 165, 165, 0.5);
          backdrop-filter: blur(10px);
        }

        .alert-box svg {
          width: 17px;
          height: 17px;
          flex-shrink: 0;
        }

        .divider {
          position: relative;
          margin: 22px 0 18px;
          text-align: center;
          color: #9ca3af;
          font-size: 0.8125rem;
        }

        .divider::before,
        .divider::after {
          content: "";
          position: absolute;
          top: 50%;
          width: 42%;
          height: 1px;
          background: #e5e7eb;
        }

        .divider::before { left: 0; }
        .divider::after { right: 0; }

        .access-note {
          display: none;
        }

        .footer-note {
          display: none;
        }

        .loader {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,.3);
          border-top-color: #fff;
          animation: spin .8s linear infinite;
        }

        .shake { 
          animation: shake .5s ease !important; 
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }

        @media (max-width: 900px) {
          .login-card {
            grid-template-columns: 1fr;
            max-width: 480px;
          }

          .login-left {
            padding: 40px 30px 30px;
          }

          .login-logo {
            width: 180px;
            height: 180px;
          }

          .login-right {
            padding: 30px 40px 40px;
          }
        }

        @media (max-width: 640px) {
          .login-page {
            padding: 16px;
          }

          .login-card {
            max-width: 100%;
            border-radius: 16px;
          }

          .login-left {
            padding: 30px 20px 20px;
          }

          .login-logo {
            width: 140px;
            height: 140px;
          }

          .login-right {
            padding: 22px 24px 28px;
          }

          .login-title {
            font-size: 1.2rem;
            margin-bottom: 4px;
          }

          .login-subtitle {
            font-size: .8rem;
            margin-bottom: 20px;
          }

          .field {
            margin-bottom: 16px;
          }

          .field label {
            font-size: .8rem;
            margin-bottom: 6px;
          }

          .input-wrapper input {
            padding: 10px 12px 10px 38px;
            font-size: .875rem;
          }

          .input-icon svg {
            width: 17px;
            height: 17px;
          }

          .input-action svg {
            width: 17px;
            height: 17px;
          }

          .login-button {
            padding: 11px;
            font-size: .875rem;
            margin-top: 6px;
          }

          .login-button svg {
            width: 16px;
            height: 16px;
          }

          .alert-box {
            font-size: .8rem;
            padding: 9px 11px;
            margin-top: 12px;
          }

          .alert-box svg {
            width: 15px;
            height: 15px;
          }
        }

        @media (max-width: 480px) {
          .login-page {
            padding: 12px;
          }

          .login-card {
            border-radius: 14px;
          }

          .login-left {
            padding: 24px 16px 16px;
            border-right: none;
          }

          .login-logo {
            width: 120px;
            height: 120px;
          }

          .login-right {
            padding: 16px 18px 24px;
          }

          .login-title {
            font-size: 1.1rem;
            margin-bottom: 2px;
          }

          .login-subtitle {
            font-size: .75rem;
            margin-bottom: 16px;
          }

          .field {
            margin-bottom: 14px;
          }

          .field label {
            font-size: .75rem;
            margin-bottom: 5px;
          }

          .input-wrapper input {
            padding: 9px 10px 9px 35px;
            font-size: .8125rem;
          }

          .input-icon {
            left: 11px;
          }

          .input-icon svg {
            width: 16px;
            height: 16px;
          }

          .input-action {
            right: 10px;
          }

          .input-action svg {
            width: 16px;
            height: 16px;
          }

          .login-button {
            padding: 10px;
            font-size: .8rem;
            margin-top: 4px;
          }

          .login-button svg {
            width: 15px;
            height: 15px;
          }

          .alert-box {
            font-size: .75rem;
            padding: 8px 10px;
            margin-top: 10px;
            gap: 6px;
          }

          .alert-box svg {
            width: 14px;
            height: 14px;
          }

          .divider {
            margin: 16px 0 14px;
            font-size: .75rem;
          }
        }
      `}</style>

      <div className="login-page">
        <div className="login-card" ref={formRef}>
          <div className="login-left">
            <div className="login-logo">
              <img src={logo} alt="CVB Guatemala" />
            </div>
          </div>

          <div className="login-right">
            <h1 className="login-title">Plataforma Inteligente de Activación y Predicción Operativa para Bomberos<br />PIAPO</h1>
            <p className="login-subtitle">Iniciar sesión</p>

            {/* Selector de tipo de acceso */}
            <div className="role-picker">
              <button
                type="button"
                className={`role-tab${modo === "mando" ? " active" : ""}`}
                onClick={() => { setModo("mando"); setError(""); }}
              >
                <UserIcon /> Personal de Mando
              </button>
              <button
                type="button"
                className={`role-tab${modo === "bombero" ? " active" : ""}`}
                onClick={() => { setModo("bombero"); setError(""); }}
              >
                <IdentificationIcon /> Acceso Bombero
              </button>
            </div>

            {/* ── Formulario Personal de Mando (Azure) ── */}
            {modo === "mando" && (
              <form className="login-form" onSubmit={handleSubmit}>
                <div className="field">
                  <label htmlFor="username">Usuario</label>
                  <div className="input-wrapper">
                    <div className="input-icon"><UserIcon /></div>
                    <input
                      id="username"
                      type="text"
                      placeholder="Usuario"
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      required
                      autoComplete="username"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="password">Contraseña</label>
                  <div className="input-wrapper has-action">
                    <div className="input-icon"><LockClosedIcon /></div>
                    <input
                      id="password"
                      type={verPass ? "text" : "password"}
                      placeholder="Contraseña"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required
                      autoComplete="current-password"
                    />
                    <button type="button" className="input-action" onClick={() => setVerPass(!verPass)}>
                      {verPass ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="login-button" disabled={cargando}>
                  {cargando ? <><span className="loader" /> Verificando...</> : "INICIAR SESIÓN"}
                </button>
              </form>
            )}

            {/* ── Formulario Bombero (carnet + contraseña compartida) ── */}
            {modo === "bombero" && (
              <form className="login-form" onSubmit={handleBomberoSubmit}>
                <div className="field">
                  <label htmlFor="carnet">N.º de Carnet</label>
                  <div className="input-wrapper">
                    <div className="input-icon"><IdentificationIcon /></div>
                    <input
                      id="carnet"
                      type="text"
                      placeholder="Ej. 1223"
                      value={bForm.carnet}
                      onChange={(e) => setBForm({ ...bForm, carnet: e.target.value })}
                      required
                      autoFocus
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="bpassword">Contraseña de compañía</label>
                  <div className="input-wrapper has-action">
                    <div className="input-icon"><LockClosedIcon /></div>
                    <input
                      id="bpassword"
                      type={verPass ? "text" : "password"}
                      placeholder="Contraseña compartida"
                      value={bForm.password}
                      onChange={(e) => setBForm({ ...bForm, password: e.target.value })}
                      required
                      autoComplete="off"
                    />
                    <button type="button" className="input-action" onClick={() => setVerPass(!verPass)}>
                      {verPass ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="login-button" disabled={cargando}>
                  {cargando ? <><span className="loader" /> Verificando...</> : "INGRESAR CON CARNET"}
                </button>
              </form>
            )}

            {error && (
              <div className="alert-box" role="alert">
                <ExclamationTriangleIcon />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
